/**
 * Utilidades para manejo de dispositivos
 */

import { Device } from '../services/api/models';
import { UserService } from '../services/api/user.service';

// Cache de nombres de usuario para evitar llamadas repetidas
const userNameCache: Record<string, string> = {};

/**
 * Determina si un dispositivo está online basado en su última conexión
 * @param device - Dispositivo a evaluar
 * @param thresholdMs - Umbral en milisegundos (por defecto 90 segundos)
 * @returns true si el dispositivo está online
 */
export function isDeviceOnline(device: Device, thresholdMs: number = 90000): boolean {
  if (!device.lastConnection) {
    return false;
  }

  // Normalize lastConnection to number (defensive programming)
  const lastConnectionTime = typeof device.lastConnection === 'string'
    ? new Date(device.lastConnection).getTime()
    : device.lastConnection;

  // Validate that we got a valid timestamp
  if (isNaN(lastConnectionTime)) {
    console.warn('Invalid lastConnection value:', device.lastConnection);
    return false;
  }

  const now = Date.now();
  const elapsed = now - lastConnectionTime;

  return elapsed < thresholdMs;
}

/**
 * Formatea la última conexión en formato relativo (hace X minutos/horas/días)
 * @param lastConnection - Timestamp de última conexión
 * @returns String formateado
 */
export function formatLastConnection(lastConnection: number | string | undefined): string {
  if (!lastConnection) {
    return 'Nunca';
  }

  // Normalize lastConnection to number (defensive programming)
  const lastConnectionTime = typeof lastConnection === 'string'
    ? new Date(lastConnection).getTime()
    : lastConnection;

  // Validate that we got a valid timestamp
  if (isNaN(lastConnectionTime)) {
    console.warn('Invalid lastConnection value for formatting:', lastConnection);
    return 'Fecha inválida';
  }

  const now = Date.now();
  const diffMs = now - lastConnectionTime;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'Hace unos segundos';
  } else if (diffMin < 60) {
    return `Hace ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`;
  } else if (diffHour < 24) {
    return `Hace ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`;
  } else {
    return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
  }
}

/**
 * Obtiene el nombre de usuario del cache o lo carga desde el servicio
 * @param userId - ID del usuario
 * @returns Promise con el nombre del usuario
 */
export async function getUserName(userId: string): Promise<string> {
  // Verificar cache primero
  if (userNameCache[userId]) {
    return userNameCache[userId];
  }

  try {
    const user = await UserService.getUserById(userId);
    const userName = user.username || user.email || userId;

    // Guardar en cache
    userNameCache[userId] = userName;

    return userName;
  } catch (error) {
    console.warn(`Error loading user ${userId}:`, error);
    return userId; // Fallback al ID si no se puede cargar
  }
}

/**
 * Enriquece un array de dispositivos con nombres de usuario
 * @param devices - Array de dispositivos
 * @returns Promise con array de dispositivos enriquecidos con campo 'ownerName'
 */
export async function enrichDevicesWithUserNames(devices: Device[]): Promise<(Device & { ownerName?: string })[]> {
  if (!devices || devices.length === 0) {
    return devices;
  }

  // Obtener IDs únicos de usuarios
  const uniqueUserIds = [...new Set(devices.map(d => d.user).filter(Boolean))];

  // Cargar nombres con concurrencia limitada (Chunks de 5)
  const userNames: { userId: string, name: string }[] = [];
  const CONCURRENCY_LIMIT = 5;
  
  for (let i = 0; i < uniqueUserIds.length; i += CONCURRENCY_LIMIT) {
    const chunk = uniqueUserIds.slice(i, i + CONCURRENCY_LIMIT);
    const chunkPromises = chunk.map(async (userId) => {
      const name = await getUserName(userId);
      return { userId, name };
    });
    const chunkResults = await Promise.all(chunkPromises);
    userNames.push(...chunkResults);
  }

  // Crear mapa de userId -> nombre
  const userNameMap: Record<string, string> = {};
  userNames.forEach(({ userId, name }) => {
    userNameMap[userId] = name;
  });

  // Enriquecer dispositivos
  return devices.map(device => ({
    ...device,
    ownerName: device.user ? userNameMap[device.user] : undefined
  }));
}

/**
 * Extrae la ciudad del campo location
 * @param location - String de ubicación
 * @returns Ciudad extraída
 */
export function extractCity(location: string | undefined): string {
  if (!location) {
    return 'N/A';
  }

  // Si location contiene comas, tomar la primera parte
  // Ejemplo: "Madrid, España" -> "Madrid"
  const parts = location.split(',');
  return parts[0].trim();
}

/**
 * Formatea un timestamp a fecha legible
 * @param timestamp - Timestamp en milisegundos
 * @returns Fecha formateada
 */
export function formatDate(timestamp: number | string | undefined): string {
  if (!timestamp) {
    return 'Fecha desconocida';
  }

  // Normalize timestamp to number (defensive programming)
  const timestampNumber = typeof timestamp === 'string'
    ? new Date(timestamp).getTime()
    : timestamp;

  // Validate that we got a valid timestamp
  if (isNaN(timestampNumber)) {
    console.warn('Invalid timestamp value for formatting:', timestamp);
    return 'Fecha inválida';
  }

  const date = new Date(timestampNumber);
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}

/**
 * Limpia el cache de nombres de usuario
 */
export function clearUserNameCache(): void {
  Object.keys(userNameCache).forEach(key => delete userNameCache[key]);
}
